import React from "react";
import PropTypes from "prop-types";
import { Grid, Typography, Divider } from "@material-ui/core";
import clsx from "clsx";
import moment from "moment-timezone";
import { useStyles } from "./Receipts.styles";

/**
 * Placeholder text rendered in place of a required field whose value is
 * unavailable for a Payment_Transaction (Requirements 4.8, 6.8).
 */
export const UNAVAILABLE_PLACEHOLDER = "Unavailable";

/** Payment-method display strings (match the BFF ReceiptPaymentVm.Method). */
export const PAYMENT_METHODS = {
  CREDIT: "Credit Card",
  CASH: "Cash",
  MIXED: "Cash and Credit Card",
};

/**
 * Determines whether a value is "present" for receipt display purposes.
 *
 * A present value is any value that is not null and not undefined. Critically,
 * a numeric 0 (e.g. an amount of $0.00) IS present and must be rendered, while
 * null/undefined is absent and must be omitted entirely (Requirements 5.3,
 * 7.1-7.7).
 *
 * @param {unknown} value The candidate value.
 * @returns {boolean} True when the value is present.
 */
const isPresent = (value) => value !== null && value !== undefined;

/**
 * Formats a numeric amount as a two-decimal monetary value, preserving the
 * sign so that negative validation discounts render as e.g. "-$5.00".
 *
 * @param {number} amount The monetary amount.
 * @returns {string} The formatted currency string.
 */
export const formatCurrency = (amount) => {
  const value = Number(amount);
  if (value < 0) {
    return `-$${Math.abs(value).toFixed(2)}`;
  }
  return `$${value.toFixed(2)}`;
};

/**
 * Formats a date/time value for display. Returns null when the value is
 * absent so the caller can render the unavailable placeholder instead.
 *
 * @param {string|Date} dt The date/time value (facility timezone, from the BFF).
 * @returns {string|null} The formatted date/time, or null when absent.
 */
const formatDateTime = (dt) => {
  if (!isPresent(dt) || dt === "") {
    return null;
  }
  const parsed = moment(dt);
  return parsed.isValid() ? parsed.format("MM/DD/YYYY HH:mm") : null;
};

/**
 * The itemized body of a receipt panel: transaction information, the fees
 * breakdown, the total, and the payment-method block.
 *
 * Line items are supplied pre-filtered and pre-ordered by the BFF view model
 * (only present items, contiguous Order), so absent optional items leave no
 * label, no "N/A", and no blank row (Requirements 5.1-5.7, 7.1-7.7). Required
 * transaction/payment fields that are unavailable render an unavailable
 * placeholder while the remaining fields still render (Requirements 4.8, 6.8).
 * EFTPOS data is never rendered (Requirement 4.9): the view model carries no
 * EFTPOS fields and none are referenced here.
 */
const ReceiptBody = ({ receipt }) => {
  const classes = useStyles();

  if (!receipt) {
    return null;
  }

  const {
    transactionId,
    rate,
    device,
    entryTime,
    paidTime,
    parkingTime,
    payOnEntryOfferName,
    isPayOnEntry,
    lineItems,
    total,
    payment,
  } = receipt;

  // A row whose value is a required field: render the value when present,
  // otherwise the unavailable placeholder (still rendering the row). The stable
  // testId is also applied as a className so acceptance/automation tests can
  // target the value cell by CSS selector.
  const RequiredRow = ({ label, value, testId }) => (
    <>
      <Grid item xs={6} className={clsx(classes.label, testId ? `${testId}-label` : null)}>
        {label}
      </Grid>
      <Grid
        item
        xs={6}
        className={clsx(
          classes.value,
          testId,
          !isPresent(value) || value === "" ? classes.placeholder : null
        )}
        data-testid={testId}
      >
        {isPresent(value) && value !== "" ? value : UNAVAILABLE_PLACEHOLDER}
      </Grid>
    </>
  );

  // A plain label/value row (used for present, optional values only). The stable
  // testId is also applied as a className for automation targeting.
  const Row = ({ label, value, testId }) => (
    <>
      <Grid item xs={6} className={clsx(classes.label, testId ? `${testId}-label` : null)}>
        {label}
      </Grid>
      <Grid item xs={6} className={clsx(classes.value, testId)} data-testid={testId}>
        {value}
      </Grid>
    </>
  );

  const formattedEntryTime = formatDateTime(entryTime);
  const formattedPaidTime = formatDateTime(paidTime);

  const orderedLineItems = Array.isArray(lineItems)
    ? [...lineItems].sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
    : [];

  const method = payment?.method;
  const involvesCredit =
    method === PAYMENT_METHODS.CREDIT || method === PAYMENT_METHODS.MIXED;
  const involvesCash =
    method === PAYMENT_METHODS.CASH || method === PAYMENT_METHODS.MIXED;

  return (
    <Grid
      container
      direction="column"
      alignItems="center"
      className={clsx(classes.receiptContainer, "receipt-body")}
      data-testid="receipt-body"
    >
      {/* Transaction information block */}
      <Grid container spacing={1} className={clsx(classes.container, "receipt-transaction-info")}>
        {/* Pay on Entry Offer — rendered at the top (above Transaction ID) only
            when the ticket was created from a POE offer. */}
        {isPresent(payOnEntryOfferName) && payOnEntryOfferName !== "" && (
          <Row
            label="Pay on Entry Offer:"
            value={payOnEntryOfferName}
            testId="receipt-poe-offer"
          />
        )}
        <RequiredRow
          label="Transaction ID:"
          /* Rendered character-for-character; String() avoids any numeric
             reformatting/truncation of the long transaction id. */
          value={isPresent(transactionId) ? String(transactionId) : transactionId}
          testId="receipt-transaction-id"
        />
        <RequiredRow label="Rate:" value={rate} testId="receipt-rate" />
        <RequiredRow label="Device:" value={device} testId="receipt-device" />
        <RequiredRow
          label="Entry Date/Time:"
          value={formattedEntryTime}
          testId="receipt-entry-time"
        />
        <RequiredRow
          /* POE tickets show "Exit By" (the entry time plus the offer's paid duration), matching the
             device receipt; standard tickets show the actual "Exit/Paid Time". */
          label={isPayOnEntry ? "Exit By:" : "Exit/Paid Time:"}
          value={formattedPaidTime}
          testId="receipt-paid-time"
        />
        {/* Parking Time is derived from entry/paid; render when available. Not shown for
            Pay-On-Entry tickets, whose Exit/Paid Time reflects the offer duration, not an exit (AC-2). */}
        {!isPayOnEntry && isPresent(parkingTime) && parkingTime !== "" && (
          <Row label="Parking Time:" value={parkingTime} testId="receipt-parking-time" />
        )}
      </Grid>

      <Divider className={clsx(classes.divider)} />

      {/* Fees block: only contiguous present line items. */}
      <Grid container spacing={1} className={clsx(classes.container, "receipt-fees")}>
        {orderedLineItems.map((item, index) => (
          <Row
            key={`${item.label}-${item.order ?? index}`}
            label={item.label}
            value={formatCurrency(item.amount)}
            testId={`receipt-line-item-${index}`}
          />
        ))}
      </Grid>

      <Divider className={clsx(classes.divider)} />

      {/* Total */}
      <Grid container spacing={1} className={clsx(classes.container, "receipt-total-row")}>
        <Grid item xs={6} className={clsx(classes.totalLabel, "receipt-total-label")}>
          Total:
        </Grid>
        <Grid
          item
          xs={6}
          className={clsx(classes.totalValue, "receipt-total")}
          data-testid="receipt-total"
        >
          {formatCurrency(total)}
        </Grid>
      </Grid>

      {/* Payment-method block */}
      {isPresent(method) && (
        <>
          <Divider className={clsx(classes.divider)} />
          <Typography
            variant="subtitle1"
            gutterBottom
            className={clsx(classes.sectionTitle, "receipt-payment-title")}
          >
            Payment
          </Typography>
          <Grid container spacing={1} className={clsx(classes.container, "receipt-payment")}>
            <RequiredRow
              label="Payment Method:"
              value={method}
              testId="receipt-payment-method"
            />

            {involvesCredit && (
              <>
                <RequiredRow
                  label="Card Type:"
                  value={payment?.cardType}
                  testId="receipt-card-type"
                />
                <RequiredRow
                  label="Card Number:"
                  value={
                    isPresent(payment?.cardLast4) && payment.cardLast4 !== ""
                      ? `**** ${payment.cardLast4}`
                      : null
                  }
                  testId="receipt-card-last4"
                />
                <RequiredRow
                  label="Authorization Code:"
                  value={payment?.authorizationCode}
                  testId="receipt-auth-code"
                />
                <RequiredRow
                  label="Credit Card Amount:"
                  value={
                    isPresent(payment?.creditCardAmount)
                      ? formatCurrency(payment.creditCardAmount)
                      : null
                  }
                  testId="receipt-credit-amount"
                />
              </>
            )}

            {involvesCash && (
              <>
                <RequiredRow
                  label="Amount Tendered:"
                  value={
                    isPresent(payment?.cashAmountTendered)
                      ? formatCurrency(payment.cashAmountTendered)
                      : null
                  }
                  testId="receipt-cash-tendered"
                />
                <RequiredRow
                  label="Change Due:"
                  value={
                    isPresent(payment?.changeDue)
                      ? formatCurrency(payment.changeDue)
                      : null
                  }
                  testId="receipt-change-due"
                />
                <RequiredRow
                  label="Short Change:"
                  value={
                    isPresent(payment?.shortChange)
                      ? formatCurrency(payment.shortChange)
                      : null
                  }
                  testId="receipt-short-change"
                />
              </>
            )}
          </Grid>
        </>
      )}
    </Grid>
  );
};

ReceiptBody.propTypes = {
  receipt: PropTypes.shape({
    transactionId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    rate: PropTypes.string,
    payOnEntryOfferName: PropTypes.string,
    isPayOnEntry: PropTypes.bool,
    device: PropTypes.string,
    entryTime: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
    paidTime: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
    parkingTime: PropTypes.string,
    lineItems: PropTypes.arrayOf(
      PropTypes.shape({
        label: PropTypes.string,
        amount: PropTypes.number,
        order: PropTypes.number,
      })
    ),
    total: PropTypes.number,
    payment: PropTypes.shape({
      method: PropTypes.string,
      cardType: PropTypes.string,
      cardLast4: PropTypes.string,
      authorizationCode: PropTypes.string,
      creditCardAmount: PropTypes.number,
      cashAmountTendered: PropTypes.number,
      changeDue: PropTypes.number,
      shortChange: PropTypes.number,
    }),
  }),
};

export default ReceiptBody;
